import { PUBLIC_STRIPE_PUBLISHABLE_KEY } from '$env/static/public';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../../tailwind.config';
import type { CreateCheckoutSessionBody } from '../../routes/api/stripe/checkout/+server';
import type { CreateSetupIntentResponse } from '../../routes/api/stripe/setup-intent/+server';
import type { UpdateCustomerBody } from '../../routes/api/stripe/customer/+server';
import type { GetPromoCodeResponse } from '../../routes/api/stripe/promo/[code]/+server';
import type { GetCouponResponse } from '../../routes/api/stripe/coupon/[id]/+server';

const { theme } = resolveConfig(tailwindConfig);

export const stripeClient =
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  typeof window === 'undefined' ? undefined : new Stripe(PUBLIC_STRIPE_PUBLISHABLE_KEY!);

export const defaultElementsOptions =
  typeof window === 'undefined'
    ? {}
    : {
        fonts: [
          {
            family: 'gilroy',
            src: `url(${window.location.origin}/api/assets/gilroy-medium.woff2) format('woff2')`,
            weight: '500',
          },
        ],
        appearance: {
          variables: {
            fontFamily: 'gilroy, system-ui, sans-serif',
            colorPrimary: `${theme.colors.primary[500]}`,
          },
          rules: {
            '.Input': {
              fontSize: '16px',
              border: `1px solid ${theme.colors.gray[100]}`,
              backgroundColor: `${theme.colors.gray[100]}`,
              boxShadow: 'none',
              padding: '18px 12px',
            },
            '.Input:focus': {
              border: '1px solid #000',
              boxShadow: 'none',
            },
            '.Input--invalid': {
              border: `1px solid ${theme.colors.error[500]}`,
              borderColor: `${theme.colors.error[500]}`,
              color: `${theme.colors.error[500]}`,
              boxShadow: 'none',
            },
          },
        },
      };

export const createCheckoutSession = async (
  body: CreateCheckoutSessionBody,
  abortController?: AbortController,
): Promise<any> => {
  const response = await fetch('/api/stripe/checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal: abortController?.signal,
  });

  if (!response.ok) {
    const data = await response.json();
    console.error('Response error', response);
    throw new Error(data.raw?.message || 'Failed to create checkout session');
  }

  return response.json();
};

export async function createSetupIntent(joinPageId: string): Promise<CreateSetupIntentResponse> {
  const response = await fetch('/api/stripe/setup-intent', {
    method: 'POST',
    body: JSON.stringify({ joinPageId }),
  });

  if (!response.ok) {
    const data = await response.json();
    if (data.redirectUrl) {
      window.location.replace(data.redirectUrl);
    }
    throw new Error('Failed to create setup intent');
  }

  return response.json();
}

export async function updateCustomer({ address }: UpdateCustomerBody) {
  if (!address?.city || !address?.country) return;

  const response = await fetch('/api/stripe/customer', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ address }),
  });

  if (!response.ok) {
    throw new Error('Failed to update customer');
  }

  return response.json();
}

export async function getPromoCode(
  code: string,
): Promise<{ data?: GetPromoCodeResponse; error?: string }> {
  const response = await fetch(`/api/stripe/promo/${code}`);

  if (!response.ok) {
    const data = await response.json();
    return { error: data.message };
  }

  return { data: await response.json() };
}

export async function getCoupon(id: string): Promise<{ data?: GetCouponResponse; error?: string }> {
  const response = await fetch(`/api/stripe/coupon/${id}`);

  if (!response.ok) {
    const data = await response.json();
    return { error: data.message };
  }

  return { data: await response.json() };
}
